import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable, computed, inject, signal } from '@angular/core';

import { COVER_GENERATION_QUEUE, createdCoverCandidate } from '@shared/models/cover-generation-queue.port';

import type { CreateProjectRequest, ProjectActivityItem, ProjectDetail, ProjectListItem } from '@api';

import { StatusCodes } from 'http-status-codes';

import { ProjectsApiError, ProjectsApiService } from '../services/api/projects.api.service';

/**
 * How one {@link ProjectsStore.addActivities} call settled. Returned rather than
 * published on the store, so the raw failure reaches exactly the caller that
 * awaited it and nothing else can overwrite it in between (S47/S65).
 */
export type AddActivitiesOutcome = { ok: false; error: unknown } | { ok: true; detail: ProjectDetail };

/**
 * How one {@link ProjectsStore.copyActivityIntoProject} call settled. Carries the appended
 * occurrence rather than a whole `ProjectDetail`: the server answers with the new card
 * alone (KT-338), and the page needs its `activityId` to open the copy's dialog.
 */
export type CopyActivityIntoProjectOutcome =
  { ok: false; error: unknown } | { ok: true; activity: ProjectActivityItem };

export interface LoadOptions {
  /**
   * Skips the loading flag and keeps a non-404 failure silent (the last good data is
   * kept); a detail 404 still surfaces. A silent request that supersedes a still-visible
   * load of the same kind behaves as visible, so its own failure is never swallowed.
   */
  readonly silent?: boolean;
}

/**
 * How one {@link ProjectsStore.removeMembership} call settled — same "returned, not
 * published" contract as {@link AddActivitiesOutcome}: the page classifies the raw
 * failure itself (404 `Project.NotFound` → S33 redirect, 404
 * `Project.ProjectActivityNotFound` → reload in place, anything else → S48 error).
 */
export type RemoveMembershipOutcome = { ok: false; error: unknown } | { ok: true; detail: ProjectDetail };

/**
 * How one {@link ProjectsStore.reorderActivities} call settled (FE-12/C5). A failure
 * restores the `previousDetail` snapshot captured before the optimistic move was
 * applied — the page's own follow-up is still a fresh {@link reloadDetail} (S18), but
 * the store's own failure path no longer leaves a half-applied local order on screen
 * in the meantime.
 */
export type ReorderActivitiesOutcome =
  { ok: false; error: unknown } | { ok: true; detail: ProjectDetail; stale?: boolean };

export type UpdateProjectFieldOutcome = { ok: false; error: unknown } | { ok: true; detail: ProjectDetail };

/**
 * Route-scoped signal store for the `projects` feature (team plan C4).
 *
 * Deliberately `providedIn: null` (not `'root'`) — the `projects` parent
 * route provides it via `providers: [ProjectsStore, PROJECTS_TRANSLOCO.provider]`
 * (Q32, see `../projects-route.providers.ts`), mirroring the route-scoped
 * Transloco precedent in `kt.routes.ts`. A
 * fresh instance (and fresh state) is created on route activation and
 * destroyed on navigation away — no cross-route state leak.
 *
 * FE-2 scope: list load (S2), create (S4/S5/S6), the client-side list
 * filter (S27), and the mutation-serialization skeleton (C4) that later
 * add/remove `ProjectDetail` mutations (FE-7/FE-10) route through.
 * FE-5 adds the detail load (S33/S25/S57) and the client-side detail
 * filter (S28). FE-6 adds the project delete (S19/S66). FE-7 adds
 * {@link addActivities} plus the deferred list reload (S12/S52/S83/S87).
 * FE-10 adds {@link removeMembership} (S16/S48), {@link reloadList}
 * (S21/S43/S52). FE-11 added
 * drag-scoped reload deferral (S17/S49/S88) and a pair-swap mutation; FE-12
 * removed that mutation in favour of {@link reorderActivities} — CDK's live
 * insertion move, applied locally immediately on drop and backed by
 * `:reorder`, which the FE now uses for both drag and drop and the arrow
 * keys (Q33/S90).
 */
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- intentionally route-scoped (Q32/C4), not tree-shakable-root
@Injectable({ providedIn: null })
export class ProjectsStore {
  readonly #api = inject(ProjectsApiService);
  readonly #coverQueue = inject(COVER_GENERATION_QUEUE);

  /**
   * Projects a just-created `ProjectDetail` into its list-row shape.
   * `activityCount` derives from `detail.activities.length` — a freshly
   * created project typically has zero activities (S6), but this must
   * reflect whatever the API actually returned, not a hardcoded `0`.
   *
   * @param detail - the just-created project as returned by the API.
   * @returns the equivalent list-row projection for `projects`/`filteredProjects`.
   */
  private static toListItem(detail: ProjectDetail): ProjectListItem {
    return {
      id: detail.id,
      name: detail.name,
      ...(typeof detail.description === 'string' ? { description: detail.description } : {}),
      activityCount: detail.activities.length,
      createdAt: detail.createdAt,
    };
  }

  readonly #projects = signal<readonly ProjectListItem[]>([]);
  readonly #isLoadingList = signal(false);
  readonly #listError = signal<unknown>(null);
  /**
   * Count of `create()` calls currently queued or in-flight (not a boolean)
   * so overlapping creates keep `isCreating` `true` until *all* of them have
   * settled — a lone `finally` on one call must not clear the flag while a
   * sibling call is still pending (C1-I-1).
   */
  readonly #createInFlightCount = signal(0);
  readonly #createError = signal<unknown>(null);
  readonly #searchQuery = signal('');
  readonly #detail = signal<ProjectDetail | null>(null);
  readonly #isLoadingDetail = signal(false);
  readonly #detailError = signal<unknown>(null);
  readonly #detailSearchQuery = signal('');
  readonly #isDragInProgress = signal(false);

  readonly isCreating = computed(() => this.#createInFlightCount() > 0);
  /**
   * `projects()` filtered by `searchQuery` (name OR description,
   * case-insensitive). Purely client-side (Q8/Q25) — duplicate names are
   * never deduplicated, both stay visible (S41).
   */
  readonly filteredProjects = computed(() => {
    const query = this.#searchQuery().trim().toLowerCase();
    if (query.length === 0) {
      return this.#projects();
    }

    return this.#projects().filter((project) => {
      const name = project.name.toLowerCase();
      const description = (project.description ?? '').toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  });
  /**
   * `detail()?.activities` filtered by `detailSearchQuery` (name OR
   * description, case-insensitive) — purely client-side, like the list
   * filter (S28). Server order (`sortOrder` ASC) is preserved.
   */
  readonly filteredDetailActivities = computed<readonly ProjectActivityItem[]>(() => {
    const activities = this.#detail()?.activities ?? [];
    const query = this.#detailSearchQuery().trim().toLowerCase();
    if (query.length === 0) {
      return activities;
    }

    return activities.filter(
      (activity) => activity.name.toLowerCase().includes(query) || activity.description.toLowerCase().includes(query),
    );
  });

  /**
   * Increments for every detail mutation. A response from an older mutation
   * must not overwrite a newer optimistic reorder or another mutation response.
   */
  #detailMutationGeneration = 0;
  /**
   * The latest mutation response held while a drag is active.
   */
  #deferredDetail: { projectId: string; detail: ProjectDetail; generation: number } | null = null;
  /** A detail-load error held until an active drag ends. */
  #deferredDetailError: { projectId: string; error: unknown; generation: number } | null = null;
  /** A detail reload requested while a drag was active (`silent` only when every deferred reload was), or `null`. */
  #deferredDetailReload: { silent: boolean } | null = null;
  /** Callers waiting for a drag-deferred detail reload to settle. */
  readonly #deferredDetailReloadResolvers: (() => void)[] = [];
  /**
   * A list reload requested while a drag was active, together with the
   * deferral session that was current when it was requested.
   */
  #deferredListReload: { session: number | null } | null = null;
  /** Full project list as last loaded from the server (`createdAt` desc, server-ordered). */
  readonly projects = this.#projects.asReadonly();
  readonly isLoadingList = this.#isLoadingList.asReadonly();
  /** `null` on success — a legitimately empty list is not an error (S2). */
  readonly listError = this.#listError.asReadonly();
  readonly createError = this.#createError.asReadonly();
  /** Client-side search term (S27/Q8/Q25) — never triggers a network call. */
  readonly searchQuery = this.#searchQuery.asReadonly();
  /**
   * The currently loaded project's detail, or `null` when nothing is loaded —
   * cleared on `:projectId` change so the previous project never flashes (S57).
   */
  readonly detail = this.#detail.asReadonly();
  readonly isLoadingDetail = this.#isLoadingDetail.asReadonly();
  /**
   * Raw detail-load failure (a {@link ProjectsApiError} for HTTP failures), or
   * `null` on success. Deliberately unclassified here: the page decides between
   * the `/projects` redirect (404 — S33), the in-page error state (400/5xx —
   * S25) and leaving it to the auth pipeline (401 — S24). A silent load only ever
   * sets it for a 404; any other silent failure leaves it untouched.
   */
  readonly detailError = this.#detailError.asReadonly();
  /** Client-side detail search term (S28) — never triggers a network call. */
  readonly detailSearchQuery = this.#detailSearchQuery.asReadonly();
  /** Whether a project activity drag is currently active (S88). */
  readonly isDragInProgress = this.#isDragInProgress.asReadonly();
  /**
   * Serializes project-detail mutations *and* list loads onto a single
   * queue — a UX safeguard (not server-side locking) so overlapping calls
   * settle, and hit the network, in submission order instead of
   * interleaving (C4). `create()` and `loadList()` both route their API
   * call through it (Correctness C4-I-1 — a `loadList()` GET and a
   * `create()` POST can no longer race at the network layer, which is what
   * previously let a successful `create()` clobber the entire list with
   * only the new project once a stale GET was discarded).
   *
   * This dispatch-level serialization — not removed, and not replaced by
   * an application-only versioning scheme (e.g. tagging each mutation with
   * a counter and reconciling after the fact) — was a deliberate choice:
   * versioning alone would still let a `create()` POST and a `loadList()`
   * GET race on the wire, only patching up the observable state
   * afterwards; queuing dispatch itself is what guarantees they can never
   * interleave *at the network layer* (Arch C6-I-6). The accepted cost is
   * latency, not correctness: a `create()` submitted while a `loadList()`
   * GET is in flight waits for that GET to settle before its POST is even
   * sent, rather than firing immediately — acceptable given the small list
   * sizes involved (Q8) (Correctness C5-I-1).
   *
   * FE-7's `addActivities`, FE-10's `removeMembership`, and FE-12's
   * `reorderActivities` route their `ProjectDetail` mutations through
   * this primitive. The detail-mutation generation rejects stale structural
   * responses. Drag-scoped deferral then holds every detail/list write until the
   * pointer gesture is complete.
   */
  #mutationQueue: Promise<unknown> = Promise.resolve();
  /**
   * A single `loadList()`-only counter (never bumped by `create()`) that
   * owns two related jobs for overlapping `loadList()` calls — previously
   * split across two separate counters (`#loadListGeneration` and
   * `#projectsEpoch`) that were always bumped together at the same point
   * and were therefore always equal; merged here into one (Arch C6-I-1):
   *
   * 1. **Spinner-clear gating** (`finally`): an older, superseded
   *    `loadList()` call must not flip `isLoadingList` back to `false`
   *    while a *newer* `loadList()` is still in flight — only the newest
   *    call's own `finally` may clear it (C3-I-1/C4-I-1).
   * 2. **Transient-write suppression** (`try`/`catch`): this is **not**
   *    what guarantees final-state correctness across overlapping
   *    `loadList()` calls — post-C4, the FIFO `#mutationQueue` already
   *    guarantees that (GETs settle, and thus apply, in submission order,
   *    so the last-submitted call's response is always the one that lands
   *    last). What this counter still guards against is a *transient*
   *    mid-settlement write: without it, an older call's GET — which the
   *    queue still runs and resolves — would briefly overwrite
   *    `#projects`/`#listError` with stale data before the newer call's
   *    own GET applies its result on top, producing an observable flicker
   *    (C1-I-4, C2-I-1).
   *
   * `loadList()` captures the current value as `epoch` for that call and
   * uses that single capture for both checks above.
   *
   * `create()` never bumps this: with the queue serializing it against
   * `loadList()`, there is no cross-operation race left to protect
   * against — bumping it on a successful `create()` used to make an
   * in-flight `loadList()` discard its own GET response entirely, silently
   * losing every pre-existing project the GET would have restored (C4-I-1,
   * fixed). The per-mutation re-evaluation this comment used to ask for has
   * happened: neither `deleteProject`, `addActivities` nor `removeMembership`
   * pairs itself with an epoch, because `#mutationQueue` plus a
   * `#detail()?.id` guard covers what an epoch would. The warning that
   * motivated it still stands, though — this pattern must not be copied
   * wholesale from `SimilarActivityStore`'s unserialized epoch precedent,
   * where the epoch *is* load-bearing for final-state correctness because
   * nothing else serializes those operations.
   */
  #loadListEpoch = 0;
  /**
   * `loadDetail()`-only counter, playing the same two roles `#loadListEpoch`
   * plays for the list: it gates the spinner clear so a superseded load cannot
   * hide a newer one's spinner, and it suppresses a stale detail write. Also
   * bumped by `clearDetail()`, so a `:projectId` change invalidates the
   * previous project's in-flight response outright (S57).
   */
  #loadDetailEpoch = 0;
  /** The project id `loadDetail()` was last called with — `reloadDetail()`'s target. */
  #loadedProjectId: string | null = null;
  /**
   * Monotonic id of the last deferral session opened by {@link deferListReload}.
   * A plain boolean cannot express *which* session a settling mutation belongs to:
   * with one, an add issued in project A's dialog session and settling after that
   * session ended would either fire the immediate `GET /v1/projects` K1 asks to
   * suppress, or be charged to whatever session happens to be open by then.
   */
  #listReloadSession = 0;
  /**
   * The session currently holding back mutation-triggered list reloads (S52/S83),
   * or `null` when nothing is deferring — set while the add-activities dialog is
   * open, so N adds cost one `GET /v1/projects` on close instead of N.
   */
  #deferringSession: number | null = null;
  /** Whether at least one add succeeded in that session, i.e. a reload is owed. */
  #hasDeferredListReload = false;

  /**
   * Loads the caller's full project list (S2: an empty list is a valid,
   * non-error result). Routes its API call through `#mutationQueue`
   * (C4-I-1) — it cannot interleave with `create()` or a sibling
   * `loadList()` at the network layer, only settle in submission order.
   *
   * @param options - see {@link LoadOptions}.
   */
  async loadList(options: LoadOptions = {}): Promise<void> {
    const silent = options.silent === true && !this.#isLoadingList();
    // Arch C6-I-1: one counter now gates both the transient-write check
    // below and the spinner-clear check in `finally` — see `#loadListEpoch`.
    const epoch = ++this.#loadListEpoch;
    if (!silent) {
      this.#isLoadingList.set(true);
      this.#listError.set(null);
    }

    try {
      const response = await this.#enqueueMutation(() => this.#api.list());
      if (epoch !== this.#loadListEpoch) {
        return;
      }

      this.#projects.set(response.items);
      this.#listError.set(null);
    } catch (error) {
      if (epoch !== this.#loadListEpoch) {
        return;
      }

      if (silent) {
        return;
      }

      // C1-I-3: keep the last-known-good list on failure — only surface the
      // error, never wipe a previously successful load.
      this.#listError.set(error);
    } finally {
      // C3-I-1/C4-I-1: an older, already-superseded `loadList()` must not
      // suppress the newer call's own spinner ownership.
      if (epoch === this.#loadListEpoch) {
        this.#isLoadingList.set(false);
      }
    }
  }

  setSearchQuery(value: string): void {
    this.#searchQuery.set(value);
  }

  /**
   * Loads one project's detail (S33/S25). Routes its GET through
   * `#mutationQueue` for the same reason `loadList()` does — so it can never
   * interleave at the network layer with a sibling list load, with the add
   * (FE-7) or remove (FE-10) mutation whose own `ProjectDetail` response must
   * win, or with FE-12's `:reorder`.
   *
   * Failure keeps `detail()` untouched and only surfaces `detailError()`; the
   * page classifies the status (404 → redirect, 400/5xx → in-page error,
   * 401 → auth pipeline). A silent load surfaces only a 404 and swallows every
   * other failure.
   *
   * @param projectId - the project to load.
   * @param options - see {@link LoadOptions}.
   */
  async loadDetail(projectId: string, options: LoadOptions = {}): Promise<void> {
    const silent = options.silent === true && !this.#isLoadingDetail();
    const epoch = ++this.#loadDetailEpoch;
    const mutationGeneration = this.#detailMutationGeneration;
    this.#loadedProjectId = projectId;
    if (!silent) {
      this.#isLoadingDetail.set(true);
      this.#detailError.set(null);
    }

    try {
      const detail = await this.#enqueueMutation(() => this.#api.detail(projectId));
      if (epoch !== this.#loadDetailEpoch || mutationGeneration !== this.#detailMutationGeneration) {
        return;
      }

      this.#applyLoadedDetail(projectId, detail, mutationGeneration);
      this.#deferredDetailError = null;
      this.#detailError.set(null);
    } catch (error) {
      if (epoch !== this.#loadDetailEpoch || mutationGeneration !== this.#detailMutationGeneration) {
        return;
      }

      if (silent && !this.#isNotFound(error)) {
        return;
      }

      if (this.#isDragInProgress()) {
        this.#deferredDetailError = { projectId, error, generation: mutationGeneration };
        return;
      }

      this.#detailError.set(error);
    } finally {
      // Only the newest load owns the spinner — an older, superseded call must
      // not clear it while the newer one is still in flight (mirrors `loadList`).
      if (epoch === this.#loadDetailEpoch) {
        this.#isLoadingDetail.set(false);
      }
    }
  }

  /**
   * Re-loads the project `loadDetail()` was last called with — the retry
   * affordance of the S25 error state, and the refresh hook FE-7/FE-10 use
   * after a successful mutation. No-ops when no project is loaded.
   *
   * @param options - see {@link LoadOptions}; a drag-deferred replay is non-silent if any deferred request was.
   */
  async reloadDetail(options: LoadOptions = {}): Promise<void> {
    const projectId = this.#loadedProjectId;
    if (projectId === null) {
      return;
    }

    if (this.#isDragInProgress()) {
      const silent = (this.#deferredDetailReload?.silent ?? true) && options.silent === true;
      this.#deferredDetailReload = { silent };
      await new Promise<void>((resolve) => this.#deferredDetailReloadResolvers.push(resolve));
      return;
    }

    await this.loadDetail(projectId, options);
  }

  /**
   * Drops the loaded detail, its error and its search term, and invalidates any
   * in-flight detail response — so a `:projectId` change cannot flash the
   * previous project's activities (S57).
   */
  clearDetail(): void {
    this.cancelDrag();
    this.#loadDetailEpoch += 1;
    this.#detailMutationGeneration += 1;
    this.#loadedProjectId = null;
    this.#deferredDetail = null;
    this.#deferredDetailError = null;
    this.#deferredDetailReload = null;
    this.#deferredListReload = null;
    this.#detail.set(null);
    this.#detailError.set(null);
    this.#detailSearchQuery.set('');
    this.#isLoadingDetail.set(false);
  }

  /** Marks the beginning of a project activity drag (S88). */
  beginDrag(): void {
    this.#isDragInProgress.set(true);
  }

  /**
   * Ends a drag and releases detail/list work that settled or was requested
   * while the pointer gesture was active.
   */
  endDrag(): void {
    this.#isDragInProgress.set(false);

    const deferredDetail = this.#deferredDetail;
    this.#deferredDetail = null;
    if (
      deferredDetail !== null &&
      deferredDetail.generation === this.#detailMutationGeneration &&
      this.#detail()?.id === deferredDetail.projectId
    ) {
      this.#detail.set(deferredDetail.detail);
    }

    const deferredDetailError = this.#deferredDetailError;
    this.#deferredDetailError = null;
    if (
      deferredDetailError !== null &&
      deferredDetailError.generation === this.#detailMutationGeneration &&
      this.#detail()?.id === deferredDetailError.projectId
    ) {
      this.#detailError.set(deferredDetailError.error);
    }

    const deferredReload = this.#deferredDetailReload;
    this.#deferredDetailReload = null;
    if (deferredReload === null) {
      this.#resolveDeferredDetailReloads();
    } else {
      // `#mutationQueue` already serializes this reload's GET behind any `:reorder` (or
      // other mutation) request still in flight from this same drag, so it always settles
      // with post-mutation server state — never a race that needs a local re-apply on top.
      void this.reloadDetail({ silent: deferredReload.silent }).finally(() => this.#resolveDeferredDetailReloads());
    }

    const deferredListReload = this.#deferredListReload;
    this.#deferredListReload = null;
    if (deferredListReload !== null && deferredListReload.session === this.#deferringSession) {
      this.#requestListReload(deferredListReload.session);
    }
  }

  /**
   * Cancels a drag while the page is being destroyed. Deferred work belongs
   * to the page that is leaving and must not start a request after teardown.
   */
  cancelDrag(): void {
    this.#isDragInProgress.set(false);
    this.#deferredDetail = null;
    this.#deferredDetailError = null;
    this.#deferredDetailReload = null;
    this.#deferredListReload = null;
    this.#resolveDeferredDetailReloads();
  }

  setDetailSearchQuery(value: string): void {
    this.#detailSearchQuery.set(value);
  }

  /**
   * Creates a project and prepends it to the list — newest first (S4).
   * No-ops on an empty/whitespace-only name instead of calling the API
   * (S5 — server-side rejection of an invalid name is a separate, later
   * concern per S9).
   *
   * @param request - the requested name (required) and description (optional).
   * @returns the created `ProjectDetail`, or `null` on validation/API failure.
   */
  async create(request: CreateProjectRequest): Promise<ProjectDetail | null> {
    const name = request.name.trim();
    // Correctness C5-I-2b (minor): clear any stale error before the
    // empty-name no-op returns, so a blank create doesn't leave a
    // previous failed create's error visible.
    this.#createError.set(null);
    if (name.length === 0) {
      return null;
    }

    const payload: CreateProjectRequest =
      request.description === undefined ? { name } : { name, description: request.description };

    this.#createInFlightCount.update((count) => count + 1);

    try {
      const detail = await this.#enqueueMutation(() => this.#api.create(payload));
      this.#projects.update((items) => [ProjectsStore.toListItem(detail), ...items]);
      // C2-I-1/C2-I-2: last-settled create wins — clear any earlier create's
      // error. `#loadListEpoch` is intentionally left untouched (C4-I-1) —
      // `#mutationQueue` already serializes this against `loadList()`, so
      // there is no in-flight GET left to protect against, and bumping it
      // here used to make a still-in-flight `loadList()` discard its own
      // response outright, silently dropping every pre-existing project.
      this.#createError.set(null);
      return detail;
    } catch (error) {
      this.#createError.set(error);
      return null;
    } finally {
      // C1-I-1: decrement rather than clear outright — `isCreating` (derived
      // from the count) only drops back to `false` once every queued/
      // in-flight create for this store has settled.
      this.#createInFlightCount.update((count) => count - 1);
    }
  }

  /**
   * Adds library activities to the project as one atomic batch and applies the
   * server's re-read `ProjectDetail` as the new detail — read-your-writes, so the
   * new row is visible without a second `GET` (S12/S87). A failure leaves
   * `detail()` exactly as it was.
   *
   * The outcome is **returned**, not published through a signal: a shared
   * error slot would make each caller's correctness depend on nothing else
   * overwriting it between the `await` resolving and the caller reading it, and
   * the raw error is only ever read by the one caller that awaited this call. It
   * classifies the status itself (404 → S33 redirect per K1, 400 →
   * `kind: 'validation'`, otherwise `kind: 'other'` — S47/S65).
   *
   * Routed through `#mutationQueue` like every other call here, so it can never
   * interleave with a list/detail load or a sibling mutation at the network
   * layer. The detail-mutation generation and the `#detail()?.id` guard below
   * keep stale responses from overwriting a newer mutation or another project.
   *
   * @param projectId - the project to add to.
   * @param activityIds - the library activity ids to add (the UI sends exactly one — S12).
   * @returns the project's updated `ProjectDetail`, or the raw failure.
   */
  async addActivities(projectId: string, activityIds: readonly string[]): Promise<AddActivitiesOutcome> {
    // Captured at entry, not read on settle: by the time the POST resolves the
    // dialog session that owns the owed reload may have ended, or a *different*
    // one may have started — and this add belongs to neither of those.
    const session = this.#deferringSession;
    const generation = ++this.#detailMutationGeneration;

    try {
      const detail = await this.#enqueueMutation(() => this.#api.addActivities(projectId, activityIds));

      // The page may have navigated to another project (or cleared the detail)
      // while the POST was in flight — never resurrect a foreign project's detail.
      this.#applyServerDetail(projectId, detail, generation);

      this.#requestListReload(session);
      return { ok: true, detail };
    } catch (error) {
      return { ok: false, error };
    }
  }

  /**
   * Duplicates one activity into a project with a single request (KT-338) and appends the
   * returned occurrence to `detail()`. Never optimistic: a failure leaves `detail()` exactly
   * as it was, and the copy either exists in the project or not at all — the server writes
   * both in one transaction.
   *
   * Shares {@link addActivities}' guards for the same reasons: the mutation queue keeps it
   * from interleaving with a load or a sibling mutation, the generation and the
   * `#detail()?.id` check keep a settled response from touching a newer mutation's state or
   * another project's detail, and the deferring session is captured at entry so the owed
   * list reload (`activityCount` changed) is attributed to the session this call belongs to.
   *
   * @param projectId - the project the copy is appended to.
   * @param activityId - the activity to copy.
   * @returns the appended occurrence, or the raw failure.
   */
  async copyActivityIntoProject(projectId: string, activityId: string): Promise<CopyActivityIntoProjectOutcome> {
    const session = this.#deferringSession;
    const generation = ++this.#detailMutationGeneration;

    try {
      const activity = await this.#enqueueMutation(() => this.#api.copyActivityIntoProject(projectId, activityId));

      this.#coverQueue.enqueue(
        createdCoverCandidate(activity.activityId, { name: activity.name, description: activity.description }),
      );
      this.#appendActivityToDetail(projectId, activity, generation);
      this.#requestListReload(session);
      return { ok: true, activity };
    } catch (error) {
      return { ok: false, error };
    }
  }

  /**
   * Breaks one membership row and applies the server's re-read `ProjectDetail` as the
   * new detail (S16) — the activity itself stays in the library. Never optimistic: a
   * failure leaves `detail()` exactly as it was, so the card stays put (S48).
   *
   * Takes only the membership id (S16): the project is read from the loaded
   * `detail()`, because that is the project the clicked card belongs to by
   * construction. Mirrors {@link addActivities} in every other respect — the
   * `#mutationQueue`, the detail-mutation generation, the `#detail()?.id` guard,
   * and `#requestListReload` for the S52 count refresh.
   *
   * @param projectActivityId - the membership row to remove (not the activity id — Q5).
   * @returns the project's updated `ProjectDetail`, or the raw failure.
   */
  async removeMembership(projectActivityId: string): Promise<RemoveMembershipOutcome> {
    const projectId = this.#detail()?.id;
    if (projectId === undefined) {
      // Unreachable from the UI — a card cannot be clicked without a loaded detail —
      // but the project id has to come from somewhere, and guessing one would DELETE
      // against the wrong project.
      return { ok: false, error: new Error('No project detail is loaded.') };
    }

    // Captured at entry for the same reason as in `addActivities`: by the time the
    // DELETE resolves, the session that owns the owed reload may have ended.
    const session = this.#deferringSession;
    const generation = ++this.#detailMutationGeneration;

    try {
      const detail = await this.#enqueueMutation(() => this.#api.removeActivity(projectId, projectActivityId));

      // The page may have navigated to another project while the DELETE was in
      // flight — never resurrect a foreign project's detail.
      this.#applyServerDetail(projectId, detail, generation);

      this.#requestListReload(session);
      return { ok: true, detail };
    } catch (error) {
      return { ok: false, error };
    }
  }

  /**
   * Moves one occurrence to `toIndex` (beszúrásos áthelyezés, Q33/S90) and applies the
   * server's re-read detail. The move is applied to the local `detail()` **immediately** —
   * synchronously, before the request is even sent — because it is the resolution of a drop
   * CDK's live reordering already showed the teacher (C5: "azonnal"), not a speculative
   * change that might still need to defer to a drag in progress: a drop *is* the end of the
   * drag it belongs to.
   *
   * The `:reorder` request carries the **whole resulting order** (D5), which is exactly the
   * locally moved snapshot — so the optimistic move is not just a visual anticipation any
   * more, it is also what gets submitted. When the move is not applicable to the loaded
   * snapshot at all (unknown id, out-of-range or no-op index, another project), there is no
   * new order to submit and no request is sent.
   *
   * On failure, the optimistic move is rolled back to the `previousDetail` snapshot captured
   * before it was applied, so a failed `:reorder` POST never leaves a half-applied local
   * order on screen, even if the page's own follow-up {@link reloadDetail} (S18) also fails.
   *
   * @param projectId - the project whose activity order changes.
   * @param projectActivityId - the occurrence to move (not the activity id — Q5).
   * @param toIndex - the 0-based target position in the full, post-move occurrence list.
   * @returns the updated detail, or the raw API failure.
   */
  async reorderActivities(
    projectId: string,
    projectActivityId: string,
    toIndex: number,
  ): Promise<ReorderActivitiesOutcome> {
    const generation = ++this.#detailMutationGeneration;
    const previousDetail = this.#detail();
    const projectActivityIds = this.#reorderDetailLocally(projectId, projectActivityId, toIndex);

    if (projectActivityIds === null) {
      return previousDetail === null
        ? { ok: false, error: new Error('No project detail is loaded to reorder.') }
        : { ok: true, detail: previousDetail, stale: true };
    }

    try {
      const detail = await this.#enqueueMutation(() => this.#api.reorderActivities(projectId, projectActivityIds));
      const applied = this.#applyServerDetail(projectId, detail, generation);
      return { ok: true, detail, stale: !applied };
    } catch (error) {
      if (previousDetail !== null) {
        this.#applyMutationDetail(projectId, previousDetail, generation);
      }
      return { ok: false, error };
    }
  }

  async renameProject(projectId: string, name: string): Promise<UpdateProjectFieldOutcome> {
    return this.#updateProjectField(projectId, () => this.#api.updateName(projectId, name));
  }

  async changeProjectDescription(projectId: string, description: string | null): Promise<UpdateProjectFieldOutcome> {
    return this.#updateProjectField(projectId, () => this.#api.updateDescription(projectId, description));
  }

  /**
   * Marks the project list stale after a mutation this store did not perform itself —
   * today the project-side activity delete, whose `activityCount` change can span
   * several projects (S21/S43/S52).
   *
   * Deliberately **not** a `loadList()` alias: it routes through the same
   * `#requestListReload` the membership mutations use, so a reload requested while the
   * add dialog is deferring is owed to that dialog's close instead of firing a
   * `GET /v1/projects` behind an open dialog (S83).
   */
  reloadList(): void {
    this.#requestListReload(this.#deferringSession);
  }

  /**
   * Starts holding back mutation-triggered list reloads (S52/S83) — called when
   * the add-activities dialog opens. Any reload owed from an earlier deferral is
   * dropped: a fresh dialog session starts with a clean slate.
   *
   * Each call opens a *new* session (see `#listReloadSession`), so an add
   * still in flight from a previous one cannot be charged to this one. One
   * deferring session at a time is assumed, and FE-10 kept that true: the only
   * caller is still the add-activities dialog. FE-10's other mutating surfaces —
   * {@link removeMembership} and the activity detail dialog — deliberately do **not**
   * defer (a removal is a single click with an immediately visible result, so there is
   * no N-mutations-per-dialog cost to amortize), so no second session can end the one
   * the add dialog still owns.
   */
  deferListReload(): void {
    this.#listReloadSession += 1;
    this.#deferringSession = this.#listReloadSession;
    this.#hasDeferredListReload = false;
  }

  /**
   * Ends the deferring session and issues **exactly one** `loadList()` — but only
   * when at least one mutation actually succeeded while deferring (S83). Called
   * when the add-activities dialog closes.
   */
  async flushDeferredListReload(): Promise<void> {
    this.#deferringSession = null;
    if (!this.#hasDeferredListReload) {
      return;
    }

    this.#hasDeferredListReload = false;
    if (this.#isDragInProgress()) {
      this.#deferredListReload = { session: null };
      return;
    }

    await this.loadList();
  }

  /**
   * Ends the deferring session and drops the owed reload without issuing it — the
   * destroy and `:projectId`-change paths (K1: cancel, not flush — S86), and the
   * mutation-404 path where the page redirects to a list it is about to load
   * anyway. An add still in flight when this runs owes nothing either: its
   * captured session no longer matches, so it is dropped rather than escalated
   * into the immediate `GET /v1/projects` that K1 asks to suppress.
   */
  cancelDeferredListReload(): void {
    this.#deferringSession = null;
    this.#hasDeferredListReload = false;
  }

  /**
   * Deletes a project and drops its row from the list (S19). The row is removed
   * only *after* the server confirms — never optimistically — so a `404`/`5xx`
   * failure leaves the card exactly where it was (S66); the caller decides how
   * to surface the failure.
   *
   * Routed through `#mutationQueue` like every other API call here, so it can
   * neither interleave with a `loadList()` GET nor with a `create()` POST at the
   * network layer. No epoch counter is paired with it (unlike `loadList()`):
   * this mutation writes a *targeted* removal rather than replacing the whole
   * list, so a later-settling list load simply re-states the server's truth —
   * there is no stale wholesale write to suppress.
   *
   * Invariant: only `#projects` is touched — `#detail` is deliberately left
   * untouched, and that is only sound because the FE-6 delete confirmation is
   * reachable exclusively from `ProjectsListPage`, never from
   * `ProjectDetailPage`, so `detail()` is either `null` or points at a *different*
   * project — still true after FE-10, which adds no project-delete entry point to the
   * detail page. A later "delete from detail" flow must clear `#detail` itself instead
   * of assuming this method already left it consistent.
   *
   * @param projectId - the project to delete.
   * @returns `true` when the server confirmed the delete, `false` on failure.
   */
  async deleteProject(projectId: string): Promise<boolean> {
    try {
      await this.#enqueueMutation(() => this.#api.delete(projectId));
      this.#projects.update((items) => items.filter((item) => item.id !== projectId));
      return true;
    } catch {
      return false;
    }
  }

  async #updateProjectField(projectId: string, call: () => Promise<ProjectDetail>): Promise<UpdateProjectFieldOutcome> {
    const generation = ++this.#detailMutationGeneration;

    try {
      const detail = await this.#enqueueMutation(call);
      this.#applyServerDetail(projectId, detail, generation);
      return { ok: true, detail };
    } catch (error) {
      return { ok: false, error };
    }
  }

  /**
   * Applies a load response unless a newer detail mutation was submitted, or
   * holds it until an active drag ends.
   *
   * @param projectId The Project id value.
   * @param detail The Detail value.
   * @param generation The Generation value.
   */
  #applyLoadedDetail(projectId: string, detail: ProjectDetail, generation: number): void {
    if (generation !== this.#detailMutationGeneration) {
      return;
    }

    this.#rememberCovers(detail.activities);

    if (this.#isDragInProgress()) {
      this.#deferredDetail = { projectId, detail, generation };
      return;
    }

    this.#detail.set(detail);
  }

  /**
   * Applies a mutation response the server answered with a `ProjectDetail`, and hands its
   * covers to the queue when it applied. A rollback to an earlier snapshot must not use this:
   * its covers can be older than what the queue already knows.
   *
   * @returns Whether the response applied.
   * @param projectId The Project id value.
   * @param detail The Detail value.
   * @param generation The Generation value.
   */
  #applyServerDetail(projectId: string, detail: ProjectDetail, generation: number): boolean {
    const applied = this.#applyMutationDetail(projectId, detail, generation);

    if (applied) {
      this.#rememberCovers(detail.activities);
    }

    return applied;
  }

  #rememberCovers(activities: readonly ProjectActivityItem[]): void {
    for (const { activityId, cover, coverGeneration } of activities) {
      this.#coverQueue.remember(activityId, { cover, coverGeneration });
    }
  }

  /**
   * Applies a current mutation response, or holds it until an active drag ends.
   *
   * @returns The result of this operation.
   * @param projectId The Project id value.
   * @param detail The Detail value.
   * @param generation The Generation value.
   */
  #applyMutationDetail(projectId: string, detail: ProjectDetail, generation: number): boolean {
    if (generation !== this.#detailMutationGeneration || this.#detail()?.id !== projectId) {
      return false;
    }

    this.#deferredDetailError = null;
    this.#detailError.set(null);
    if (this.#isDragInProgress()) {
      this.#deferredDetail = { projectId, detail, generation };
      return true;
    }

    this.#detail.set(detail);
    return true;
  }

  /**
   * Appends one occurrence to the loaded detail and routes it through
   * {@link #applyMutationDetail}, so a copy that settles during a drag, after a newer
   * mutation, or after a navigation to another project is handled exactly like any other
   * mutation response.
   *
   * @param projectId - the project the occurrence belongs to.
   * @param activity - the occurrence the server appended.
   * @param generation - the detail-mutation generation this copy was issued in.
   */
  #appendActivityToDetail(projectId: string, activity: ProjectActivityItem, generation: number): void {
    const current = this.#detail();

    if (current === null) {
      return;
    }

    const applied = this.#applyMutationDetail(
      projectId,
      { ...current, activities: [...current.activities, activity] },
      generation,
    );

    if (applied) {
      this.#rememberCovers([activity]);
    }
  }

  /**
   * Applies an insertion-style move (`moveItemInArray`) to the local detail snapshot —
   * the optimistic half of {@link reorderActivities} — and returns the resulting occurrence
   * order, which is what the `:reorder` request submits (D5). A `toIndex` out of range, an
   * unknown `projectActivityId`, a no-op move, or a project mismatch all leave `detail()`
   * untouched and return `null`: this is a local visual anticipation of the server's own
   * move, not a place to invent one.
   *
   * @returns the moved order, or `null` when the move does not apply to the snapshot.
   * @param projectId The Project id value.
   * @param projectActivityId The Project activity id value.
   * @param toIndex The To index value.
   */
  #reorderDetailLocally(projectId: string, projectActivityId: string, toIndex: number): string[] | null {
    let reordered: string[] | null = null;

    this.#detail.update((detail) => {
      if (detail?.id !== projectId) {
        return detail;
      }

      const fromIndex = detail.activities.findIndex((activity) => activity.projectActivityId === projectActivityId);
      if (fromIndex === -1 || toIndex === fromIndex || toIndex < 0 || toIndex >= detail.activities.length) {
        return detail;
      }

      const activities = [...detail.activities];
      moveItemInArray(activities, fromIndex, toIndex);
      reordered = activities.map((activity) => activity.projectActivityId);
      return { ...detail, activities };
    });

    return reordered;
  }

  /**
   * The `reloadList()` behaviour of a successful membership mutation (S52),
   * decided by the deferral session the mutation was issued in. A reload
   * without a dialog is held while a drag is active and resumed afterwards.
   *
   * @param session - the value of `#deferringSession` when the mutation started.
   */
  #requestListReload(session: number | null): void {
    if (session === null) {
      if (this.#isDragInProgress()) {
        this.#deferredListReload = { session };
        return;
      }

      void this.loadList();
      return;
    }

    if (session !== this.#deferringSession) {
      return;
    }

    this.#hasDeferredListReload = true;
  }

  #isNotFound(error: unknown): boolean {
    return error instanceof ProjectsApiError && error.status === Number(StatusCodes.NOT_FOUND);
  }

  #resolveDeferredDetailReloads(): void {
    const resolvers = this.#deferredDetailReloadResolvers.splice(0);
    for (const resolve of resolvers) {
      resolve();
    }
  }

  #enqueueMutation<T>(task: () => Promise<T>): Promise<T> {
    // Arch C6-I-2: `#mutationQueue` is always reset below to a
    // never-rejecting promise, so a rejection handler here would be dead
    // code — only the fulfillment branch is ever reachable.
    const run = this.#mutationQueue.then(task);
    this.#mutationQueue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }
}
